/** Structured opening hours used by contribution and place editors. */
export interface OpeningHoursData {
  weekdayOpen: string;
  weekdayClose: string;
  weekdayEnabled: boolean;
  saturdayOpen: string;
  saturdayClose: string;
  saturdayEnabled: boolean;
  sundayOpen: string;
  sundayClose: string;
  sundayEnabled: boolean;
  is24_7: boolean;
}

export const DEFAULT_OPENING_HOURS: OpeningHoursData = {
  weekdayOpen: '08:00',
  weekdayClose: '17:00',
  weekdayEnabled: true,
  saturdayOpen: '09:00',
  saturdayClose: '14:00',
  saturdayEnabled: false,
  sundayOpen: '',
  sundayClose: '',
  sundayEnabled: false,
  is24_7: false,
};

export const OPENING_HOURS_MINUTE_OPTIONS = ['00', '30'] as const;
export const OPENING_HOURS_HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

export type AmPmPeriod = 'AM' | 'PM';

/** Human-readable 12-hour label for a 24-hour HH:MM value. */
export function formatTimeLabel(hhmm: string): string {
  if (!hhmm) return '';
  const { hour, minute, period } = from24HourTime(hhmm);
  return `${hour}:${minute} ${period}`;
}

export function from24HourTime(hhmm: string): { hour: number; minute: string; period: AmPmPeriod } {
  if (!hhmm) return { hour: 8, minute: '00', period: 'AM' };
  const [hStr, mStr = '00'] = hhmm.split(':');
  const hour24 = Number.parseInt(hStr, 10);
  if (Number.isNaN(hour24)) return { hour: 8, minute: '00', period: 'AM' };
  const period: AmPmPeriod = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return { hour: hour12, minute: mStr.padStart(2, '0'), period };
}

export function to24HourTime(hour12: number, minute: string, period: AmPmPeriod): string {
  let hour24 = hour12 % 12;
  if (period === 'PM') hour24 += 12;
  return `${String(hour24).padStart(2, '0')}:${minute.padStart(2, '0')}`;
}

/** Closing time must be strictly after opening time (same day). */
export function isValidHoursRange(open: string, close: string): boolean {
  return !!open && !!close && close > open;
}

export function isOpeningHoursDataValid(data: OpeningHoursData): boolean {
  if (data.is24_7) return true;
  const ranges: Array<[boolean, string, string]> = [
    [data.weekdayEnabled, data.weekdayOpen, data.weekdayClose],
    [data.saturdayEnabled, data.saturdayOpen, data.saturdayClose],
    [data.sundayEnabled, data.sundayOpen, data.sundayClose],
  ];
  for (const [enabled, open, close] of ranges) {
    if (enabled && !isValidHoursRange(open, close)) return false;
  }
  return true;
}

function normalizeTime(value: string): string {
  const [hours, minutes] = value.split(':');
  return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
}

/** Convert structured hours to OSM opening_hours text stored in the database. */
export function formatOpeningHoursToOSM(data: OpeningHoursData): string {
  if (data.is24_7) return '24/7';

  const parts: string[] = [];
  if (data.weekdayEnabled && isValidHoursRange(data.weekdayOpen, data.weekdayClose)) {
    parts.push(`Mo-Fr ${data.weekdayOpen}-${data.weekdayClose}`);
  }
  if (data.saturdayEnabled && isValidHoursRange(data.saturdayOpen, data.saturdayClose)) {
    parts.push(`Sa ${data.saturdayOpen}-${data.saturdayClose}`);
  }
  if (data.sundayEnabled && isValidHoursRange(data.sundayOpen, data.sundayClose)) {
    parts.push(`Su ${data.sundayOpen}-${data.sundayClose}`);
  }
  return parts.join('; ');
}

/** Human-readable summary for the editor preview. */
export function formatOpeningHoursHuman(data: OpeningHoursData): string {
  if (data.is24_7) return 'Open 24 hours, every day';

  const parts: string[] = [];
  if (data.weekdayEnabled && isValidHoursRange(data.weekdayOpen, data.weekdayClose)) {
    parts.push(`Mon–Fri ${formatTimeLabel(data.weekdayOpen)} – ${formatTimeLabel(data.weekdayClose)}`);
  }
  if (data.saturdayEnabled && isValidHoursRange(data.saturdayOpen, data.saturdayClose)) {
    parts.push(`Sat ${formatTimeLabel(data.saturdayOpen)} – ${formatTimeLabel(data.saturdayClose)}`);
  }
  if (data.sundayEnabled && isValidHoursRange(data.sundayOpen, data.sundayClose)) {
    parts.push(`Sun ${formatTimeLabel(data.sundayOpen)} – ${formatTimeLabel(data.sundayClose)}`);
  }
  return parts.join(' · ');
}

/** Parse OSM opening_hours text back into the structured editor state. */
export function parseOpeningHoursFromOSM(raw: string | null | undefined): OpeningHoursData {
  const data: OpeningHoursData = { ...DEFAULT_OPENING_HOURS };

  if (!raw?.trim()) {
    return {
      ...data,
      weekdayEnabled: false,
    };
  }

  const text = raw.trim();
  if (text.toLowerCase() === '24/7' || text.toLowerCase() === '24hours') {
    return {
      ...data,
      is24_7: true,
      weekdayEnabled: false,
      saturdayEnabled: false,
      sundayEnabled: false,
    };
  }

  let parsedAny = false;
  const rules = text.split(';').map((rule) => rule.trim()).filter(Boolean);

  for (const rule of rules) {
    const timeMatch = rule.match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
    if (!timeMatch || timeMatch.index == null) continue;

    const open = normalizeTime(timeMatch[1]);
    const close = normalizeTime(timeMatch[2]);
    const dayPart = rule.slice(0, timeMatch.index).trim().replace(/,$/, '').replace(/\s+/g, '');

    if (!dayPart) {
      data.weekdayOpen = open;
      data.weekdayClose = close;
      data.weekdayEnabled = true;
      parsedAny = true;
      continue;
    }

    if (/^Mo-Fr$/i.test(dayPart)) {
      data.weekdayOpen = open;
      data.weekdayClose = close;
      data.weekdayEnabled = true;
      parsedAny = true;
    } else if (/^Sa$/i.test(dayPart) || /^Saturday$/i.test(dayPart)) {
      data.saturdayOpen = open;
      data.saturdayClose = close;
      data.saturdayEnabled = true;
      parsedAny = true;
    } else if (/^Su$/i.test(dayPart) || /^Sunday$/i.test(dayPart)) {
      data.sundayOpen = open;
      data.sundayClose = close;
      data.sundayEnabled = true;
      parsedAny = true;
    } else if (/^Mo-Su$/i.test(dayPart)) {
      data.weekdayOpen = open;
      data.weekdayClose = close;
      data.weekdayEnabled = true;
      data.saturdayOpen = open;
      data.saturdayClose = close;
      data.saturdayEnabled = true;
      data.sundayOpen = open;
      data.sundayClose = close;
      data.sundayEnabled = true;
      parsedAny = true;
    }
  }

  if (parsedAny) {
    data.is24_7 = false;
  }

  return data;
}

/** Build OSM text from today's parsed place hours (best-effort prefill). */
export function openingHoursFromPlaceHours(hours: {
  open?: string | null;
  close?: string | null;
} | null | undefined): string {
  if (!hours?.open) return '';
  if (hours.close === '24:00' || hours.open === '24/7') return '24/7';
  if (!hours.close) return '';
  return formatOpeningHoursToOSM({
    ...DEFAULT_OPENING_HOURS,
    weekdayOpen: hours.open,
    weekdayClose: hours.close,
    weekdayEnabled: true,
  });
}

/** Prefer stored OSM opening_hours; fall back to parsed today's hours. */
export function openingHoursPrefill(
  openingHours?: string | null,
  hours?: { open?: string | null; close?: string | null } | null,
): string {
  if (openingHours?.trim()) return openingHours.trim();
  return openingHoursFromPlaceHours(hours);
}
